import {
  adminMenuData,
  getClinicMenuData,
  getProviderMenuData,
  getClinicGroupMenuData,
  getAdSupplierMenuData,
  getAdSupplierGroupMenuData,
  MenuItemData,
} from '@/data/menuData'
import { useViewMode, type ViewMode } from '@/stores/viewMode'
import { useMemo } from 'react'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { useMe } from '@/hooks/useMe'
import MenuItem from './MenuItem'
import { filterMenuItem } from './filterMenuItem'

interface MenuProps {
  onClose?: () => void
}

export default function Menu({ onClose }: MenuProps) {
  const { mode, id } = useViewMode()
  const { hasPermission, isLoading } = useMyPermissions()
  const { user } = useMe()
  const currentMode = mode as ViewMode

  // Get menu data based on current view mode, injecting current scope id
  // so sidebar links resolve to `/clinic/:id/info` etc.
  // NOTE: keep this useMemo call BEFORE any early return so hook order is stable.
  const menuData = useMemo<MenuItemData[]>(() => {
    switch (currentMode) {
      case 'provider':
        return id ? getProviderMenuData(id) : []
      case 'clinic':
        return id ? getClinicMenuData(id) : []
      case 'clinic_group':
        return id ? getClinicGroupMenuData(id) : []
      case 'ad':
        return id ? getAdSupplierMenuData(id) : []
      case 'ad_supplier_group':
        return id ? getAdSupplierGroupMenuData(id) : []
      case 'admin':
      case 'default':
      default:
        return adminMenuData
    }
  }, [currentMode, id])

  if (isLoading) {
    return (
      <nav className="flex-1 px-2 py-4">
        <div className="animate-pulse">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="h-10 bg-gray-200 rounded mb-2" />
          ))}
        </div>
      </nav>
    )
  }

  const userRoleType = user?.role_type
  const userScopes = user?.scopes

  // Filter menu items based on role, scopes, permissions, and hierarchical access
  const filteredMenuData = menuData.filter((item) =>
    filterMenuItem(item, hasPermission, userRoleType, userScopes, currentMode),
  )

  return (
    <nav className="flex-1 px-2 py-4" data-cobo-id="nav-system-tools" data-cobo-global>
      <ul>
        {filteredMenuData.map((item) => (
          <MenuItem key={item.label} item={item} onClose={onClose} />
        ))}
      </ul>
    </nav>
  )
}
